import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { COLLECTIONS } from "@/data/site";
import { EASE, Reveal, SectionHeading } from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";

export default function Collections() {
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="collections-page" className="bg-ivory">
      <section className="noise-overlay bg-obsidian pb-20 pt-40">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <SectionHeading
            light
            align="left"
            eyebrow="The ShilpStones Catalogue"
            title="Every Collection, Straight from the Brochure"
            script="Functional Today. Beautiful Forever."
            description="Wall art & home decor, corporate gifting, utility, hotel & resort, kitchen & vanity, office décor, flooring, parking and front elevation — the complete ShilpStones range, browsable right here."
          />
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {COLLECTIONS.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 0.1}>
                <button
                  onClick={() => setSelected(c)}
                  data-testid={`collection-card-${c.slug}`}
                  className="group relative block w-full overflow-hidden rounded-xl text-left spotlight-frame"
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
                  <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-gold/0 transition-all duration-500 group-hover:ring-gold/40" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <span className="font-display text-[10px] tracking-[0.3em] text-gold-soft">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-1 font-serif text-2xl font-medium text-stone-100 transition-colors group-hover:text-gold-glow">
                      {c.title}
                    </h3>
                    <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-400">{c.tagline}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => setSelected(null)}
            data-testid="collection-lightbox"
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="relative grid max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-charcoal-card ring-1 ring-gold/25 md:grid-cols-2"
            >
              <button
                onClick={() => setSelected(null)}
                data-testid="collection-lightbox-close"
                className="absolute right-4 top-4 z-10 rounded-full bg-obsidian/70 p-2 text-stone-300 backdrop-blur transition-colors hover:text-gold"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div>
                <img src={selected.image} alt={selected.title} className="h-full w-full object-cover object-top" />
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-10">
                <span className="eyebrow-light">{selected.tagline}</span>
                <h3 className="mt-3 font-serif text-3xl font-medium text-stone-100 sm:text-4xl">{selected.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-stone-400">{selected.desc}</p>
                <ul className="mt-7 space-y-3">
                  {selected.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-3 text-sm text-stone-300">
                      <span className="rounded-full bg-gold/15 p-1">
                        <Check className="h-3.5 w-3.5 text-gold" />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <WhatsAppCTA
                  label={`Enquire — ${selected.title}`}
                  message={`Hello ShilpStones Creations, I saw your ${selected.title} collection in the brochure and would like to know more.`}
                  context={`Collection inquiry: ${selected.title}`}
                  testId="collection-whatsapp-cta"
                  className="mt-8 w-fit"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
